import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js'
import { loadStripe } from '@stripe/stripe-js'
import { useEffect, useState } from 'react'
import styled from 'styled-components'

import NavigationButtons from '../../Common/NavigationButton'

const StyledStripeComponent = styled.div`
  margin: 1.25rem 0 2rem;
`

const StyledStripeLogo = styled.div`
  display: flex;
  justify-content: center;
  margin: 2rem 0 0;

  & > svg {
    display: block;
    max-width: 162px;
  }
`

const StripeComponent = props => {
  const { orderId, createPaymentIntent, stripeData } = props
  const { paymentIntent, clientSecret, stripeKey, stripeEntity } = stripeData || {}
  const [stripePromise, setStripePromise] = useState(null)

  const getStripePromise = async stripeKey => {
    const stripePromise = await loadStripe(stripeKey, {
      ...(stripeEntity === 'nz' && {
        betas: ['nz_bank_account_beta_2'],
        apiVersion: '2023-08-16;nz_bank_account_beta=v2',
      }),
    })

    setStripePromise(stripePromise)
  }

  useEffect(() => {
    if (!orderId) {
      return
    }

    createPaymentIntent({ orderId })
  }, [orderId])

  useEffect(() => {
    if (!stripeKey) {
      return
    }

    getStripePromise(stripeKey)
  }, [stripeKey])

  if (!orderId || !paymentIntent || !clientSecret || !stripeKey || !stripePromise) {
    return null
  }

  return (
    <Elements key={clientSecret} stripe={stripePromise} options={{ clientSecret }}>
      <CheckoutForm {...props} />
    </Elements>
  )
}

const CheckoutForm = props => {
  const { returnUrl, onError, onChangeGateway, showLoadingIndicator, buttonColour, buttonTextColour, onSuccess } = props
  const [isProcessing, setIsProcessing] = useState(false)

  const stripe = useStripe()
  const elements = useElements()

  const submitPayment = async () => {
    setIsProcessing(true)
    showLoadingIndicator(true)

    const result = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: returnUrl,
      },
      redirect: 'if_required',
    })

    setIsProcessing(false)
    showLoadingIndicator(false)

    if (result.error) {
      const { code = null, decline_code: declineCode = null } = result.error

      let errorTitle = 'Payment Declined.'
      let errorMessage = 'Please try an alternative payment method.'

      if (declineCode === 'insufficient_funds') {
        errorTitle = 'Payment Declined - Insufficient Funds.'
      }

      if (code === 'expired_card') {
        errorTitle = 'Payment Declined - Card Expired.'
      }

      if (code === 'incorrect_cvc' || code === 'incorrect_number') {
        errorTitle = 'Payment Declined - Incorrect Details.'
        errorMessage = 'Please re-enter details or try an alternative payment method.'
      }

      if (code === 'processing_error') {
        errorTitle = 'Payment Declined - Processing Error.'
      }

      onError(errorTitle, errorMessage)

      return
    }

    onSuccess()
  }

  if (!stripe || !elements) {
    return null
  }

  return (
    <>
      <StyledStripeComponent>
        <PaymentElement />
      </StyledStripeComponent>
      <NavigationButtons
        isContinueDisabled={isProcessing}
        preventDefaultOnBack
        hideSubmitButton={isProcessing}
        submitLabel="Pay Now"
        hideHr
        nonStick
        onSubmit={() => {
          if (!isProcessing) {
            submitPayment()
          }
        }}
        onBack={onChangeGateway}
        buttonColour={buttonColour}
        buttonTextColour={buttonTextColour}
      />
      <StyledStripeLogo>
        {/** biome-ignore lint/a11y/noSvgWithoutTitle: TODO */}
        <svg fill="none" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 162 36">
          <path
            d="M18.436 12.14H13.79v11.62h2.074v-4.493h2.57c2.593 0 4.213-1.253 4.213-3.564s-1.62-3.564-4.212-3.564Zm-.108 5.4h-2.463v-3.565h2.495c1.49 0 2.279.637 2.279 1.782s-.821 1.728-2.28 1.728l-.032.054Zm8.78-2.42a4.071 4.071 0 0 0-4.104 4.417 4.114 4.114 0 1 0 8.197 0 4.061 4.061 0 0 0-4.093-4.417Zm0 7.204c-1.318 0-2.16-1.08-2.16-2.787 0-1.706.82-2.786 2.16-2.786 1.34 0 2.16 1.08 2.16 2.786 0 1.707-.853 2.776-2.16 2.776v.01Zm12.614-1.426L38.2 15.26h-1.598l-1.512 5.638-1.533-5.638h-1.998l2.56 8.51h1.684l1.555-5.572 1.555 5.573h1.685l2.56-8.51h-1.923l-1.512 5.637Zm7.798-5.778a4.136 4.136 0 0 0-4.05 4.417 4.093 4.093 0 0 0 4.136 4.417A3.747 3.747 0 0 0 51.29 21.6l-1.61-.67a1.922 1.922 0 0 1-1.976 1.361 2.29 2.29 0 0 1-2.343-2.343h5.962v-.649c.021-2.332-1.297-4.18-3.802-4.18Zm-2.085 3.38a2.074 2.074 0 0 1 2.085-1.76 1.686 1.686 0 0 1 1.825 1.75l-3.91.01Zm9.31-1.976V15.26h-1.944v8.5h1.944V19.3a2.042 2.042 0 0 1 2.16-2.16c.237-.017.476-.017.713 0v-1.945h-.55a2.473 2.473 0 0 0-2.323 1.329Zm7.333-1.404a4.136 4.136 0 0 0-4.05 4.417 4.093 4.093 0 0 0 4.137 4.417 3.747 3.747 0 0 0 3.64-2.354l-1.664-.637a1.922 1.922 0 0 1-1.976 1.36 2.29 2.29 0 0 1-2.268-2.343h5.983v-.648c0-2.365-1.34-4.212-3.802-4.212Zm-2.084 3.38a2.074 2.074 0 0 1 2.074-1.75 1.686 1.686 0 0 1 1.825 1.75h-3.9Zm12.97-2.3a3.077 3.077 0 0 0-2.44-1.08c-2.387 0-3.748 1.998-3.748 4.417 0 2.42 1.361 4.417 3.748 4.417a3.046 3.046 0 0 0 2.44-1.08v.886h1.945V12.14h-1.944v4.06Zm0 3.618a2.159 2.159 0 0 1-2.16 2.462c-1.414 0-2.16-1.08-2.16-2.721 0-1.642.757-2.722 2.16-2.722 1.2 0 2.16.875 2.16 2.473v.508Zm12.69-4.698a3.11 3.11 0 0 0-2.494 1.08v-4.06h-1.944v11.62h1.944v-.897a3.089 3.089 0 0 0 2.452 1.08c2.376 0 3.736-2.008 3.736-4.417 0-2.408-1.317-4.406-3.693-4.406Zm-.334 7.128a2.16 2.16 0 0 1-2.16-2.463v-.507c0-1.598.907-2.473 2.16-2.473 1.404 0 2.16 1.08 2.16 2.721 0 1.642-.81 2.722-2.16 2.722Zm8.564-1.015L91.8 15.26h-2.052l3.132 8.197-.324.8a1.08 1.08 0 0 1-1.231.853 4.463 4.463 0 0 1-.648 0v1.63c.26.04.524.058.788.055a2.884 2.884 0 0 0 3.002-2.16l3.5-9.31h-2.041l-2.042 5.908ZM135 13.424a3.24 3.24 0 0 0-2.3.94l-.152-.745h-2.581v13.953l2.938-.637v-3.38c.584.489 1.322.757 2.084.756 2.095 0 4.018-1.718 4.018-5.52 0-3.477-1.944-5.367-4.007-5.367Zm-.702 8.24a1.74 1.74 0 0 1-1.382-.56v-4.44a1.768 1.768 0 0 1 1.404-.594c1.08 0 1.814 1.22 1.814 2.787 0 1.566-.745 2.808-1.836 2.808Zm10.13-8.24c-2.829 0-4.546 2.441-4.546 5.52 0 3.639 2.03 5.486 4.924 5.486a6.61 6.61 0 0 0 3.24-.789v-2.43a6.252 6.252 0 0 1-2.916.67c-1.166 0-2.16-.422-2.311-1.836h5.81v-1.08c.098-3.1-1.371-5.54-4.201-5.54Zm-1.587 4.396c0-1.36.831-1.933 1.566-1.933.734 0 1.512.572 1.512 1.933h-3.078Zm-20.801-3.391-.184-.886h-2.505V24.03h2.894v-7.107a2.024 2.024 0 0 1 2.214-.626v-2.754a1.943 1.943 0 0 0-2.419.886Zm-14.623 2.268c0-.476.389-.66 1.004-.66 1.02.023 2.02.29 2.916.779v-2.84a7.56 7.56 0 0 0-2.916-.552c-2.387 0-3.974 1.275-3.974 3.413 0 3.348 4.471 2.808 4.471 4.244 0 .562-.475.746-1.08.746a7.322 7.322 0 0 1-3.24-.972v2.905a7.97 7.97 0 0 0 3.24.691c2.441 0 4.126-1.242 4.126-3.413-.054-3.628-4.547-2.98-4.547-4.341Zm8.445-5.638-2.862.627v9.644a3.008 3.008 0 0 0 .856 2.205 2.986 2.986 0 0 0 2.19.895 4.496 4.496 0 0 0 2.063-.4V21.6c-.378.162-2.225.713-2.225-1.08V16.2h2.225v-2.527h-2.225l-.022-2.614Zm9.688 1.577 2.948-.616V9.688l-2.948.615v2.333Zm0 .983h2.948v10.41h-2.948V13.62Z"
            fill="#635BFF"
          />
          <rect x=".5" y=".5" width="161" height="35" rx="3.5" stroke="#635BFF" />
        </svg>
      </StyledStripeLogo>
    </>
  )
}

export default StripeComponent
